import _ from 'underscore';
import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Stack, PrimaryButton, DefaultButton } from '@fluentui/react';
import {
  useWizardService,
  getIsFirstStep,
  getIsLastStep,
  getCurrentStepId,
  matchStepState,
  STEP_STATES,
  WIZARD_EVENTS,
} from '@bingads-webui-react/wizard';
import { CancelButton } from '@bingads-webui-react/wizard-ui-library';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { STEP_PATHS } from './constants';

const SaveAndPublishButton = ({ adoptCoupon }) => {
  const i18n = useContext(I18nContext);

  const activity = useContext(ActivityContext);

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    if (activity) {
      activity.signal('onSaveAndPublishClicked');
    }
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isPublishing: true });
    adoptCoupon();
    send(WIZARD_EVENTS.SAVE_AND_EXIT);
  }, [activity, send, adoptCoupon]);

  const buttonText = useMemo(() => {
    const currentStep = getCurrentStepId(state);

    if (currentStep === STEP_PATHS.SUMMARY) {
      return i18n.getString(_TL_('Go live'));
    }

    return i18n.getString(_TL_('Save'));
  }, [i18n, state]);

  return (
    <PrimaryButton
      disabled={isBlocking}
      onClick={onNextClicked}
      className="step-save-and-publish-button"
    >
      {buttonText}
    </PrimaryButton>
  );
};

export const SaveAndExitButton = ({ adoptCoupon }) => {
  const i18n = useContext(I18nContext);

  const activity = useContext(ActivityContext);

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    if (activity) {
      activity.signal('onSaveAndExitButtonClicked');
    }
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isAdGroupSaving: true });
    adoptCoupon();
    send(WIZARD_EVENTS.SAVE_AND_EXIT);
  }, [activity, send, adoptCoupon]);

  return (
    <DefaultButton
      disabled={isBlocking}
      onClick={onNextClicked}
      className="step-save-and-exit-button"
    >
      {i18n.getString(_TL_('Save and exit'))}
    </DefaultButton>
  );
};

export const NextButton = ({ buttonText, disabled }) => {
  const i18n = useContext(I18nContext);

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    send(WIZARD_EVENTS.SAVE_AND_NEXT);
  }, [send]);

  return (
    <PrimaryButton
      disabled={disabled || isBlocking}
      onClick={onNextClicked}
      className="step-next-button"
    >
      {buttonText || i18n.getString(_TL_('Next'))}
    </PrimaryButton>
  );
};

export const BackButton = ({ buttonText }) => {
  const i18n = useContext(I18nContext);

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onBackClicked = useCallback(() => {
    send(WIZARD_EVENTS.BACK);
  }, [send]);

  return (
    <DefaultButton
      disabled={isBlocking}
      onClick={onBackClicked}
      className="step-back-button"
    >
      {buttonText || i18n.getString(_TL_('Back'))}
    </DefaultButton>
  );
};
BackButton.propTypes = {
  buttonText: PropTypes.string,
};
BackButton.defaultProps = {
  buttonText: undefined,
};


NextButton.propTypes = {
  buttonText: PropTypes.string,
  disabled: PropTypes.bool,
};
NextButton.defaultProps = {
  buttonText: undefined,
  disabled: false,
};

export const StepFooter = ({
  nextButtonText,
  nextDisabled,
  backButtonText,
  cancelButtonText,
  adoptCoupon,
}) => {
  const { state } = useWizardService();
  const { showRedirectToBMC } = state.context.wizardData;
  const isFirstStep = getIsFirstStep(state);
  const isLastStep = getIsLastStep(state);

  const currentStep = getCurrentStepId(state);

  return (
    <>
      {!showRedirectToBMC ?
        <Stack horizontal tokens={{ childrenGap: 16 }}>
          {!isFirstStep && <BackButton buttonText={backButtonText} />}
          {(!isLastStep ? <NextButton buttonText={nextButtonText} disabled={nextDisabled} /> : <SaveAndPublishButton adoptCoupon={adoptCoupon} />)}
          {currentStep === STEP_PATHS.TARGET_SETTINGS && <SaveAndExitButton adoptCoupon={adoptCoupon} />}
          {isFirstStep && <CancelButton buttonText={cancelButtonText} />}
        </Stack>
        :
        <Stack horizontal tokens={{ childrenGap: 16 }}>
          <CancelButton buttonText={cancelButtonText} />
        </Stack>
      }
    </>
  );
};
StepFooter.propTypes = {
  nextButtonText: PropTypes.string,
  nextDisabled: PropTypes.bool,
  backButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  adoptCoupon: PropTypes.func,
};
StepFooter.defaultProps = {
  nextButtonText: undefined,
  nextDisabled: false,
  backButtonText: undefined,
  cancelButtonText: undefined,
  adoptCoupon: _.noop,
};
